
// This file is automatically generated. Do not edit it directly.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Extend Window interface for Clerk
declare global {
  interface Window {
    Clerk?: {
      user?: {
        id: string;
      };
      session?: {
        getToken: (options?: { template?: string }) => Promise<string | null>;
      };
    };
    __SB_CLIENTS__?: { [key: string]: SupabaseClient<Database> };
    __SB_CLIENT_PROMISES__?: { [key: string]: Promise<SupabaseClient<Database>> };
  }
}

const SUPABASE_URL = "https://yaepfwttuetyalzmvzmj.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlhZXBmd3R0dWV0eWFsem12em1qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU4Mzg4ODUsImV4cCI6MjA2MTQxNDg4NX0.FqXrscs7OVzn-civNTl7XP7gN7SlqBjLFwnmBIcbftI";

// Create the base Supabase client with public (anon) key
// Stable anon client, memoized across HMR via window cache to avoid duplicate GoTrue warnings
if (typeof window !== 'undefined') {
  window.__SB_CLIENTS__ = window.__SB_CLIENTS__ || {};
}

export const supabase: SupabaseClient<Database> = (typeof window !== 'undefined' && window.__SB_CLIENTS__?.anon)
  ? window.__SB_CLIENTS__!.anon
  : createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: {
        // Prevent extra GoTrue instances from persisting sessions in this anonymous client
        persistSession: false,
        autoRefreshToken: false,
        storageKey: 'sb-calone-anon',
      },
    });

if (typeof window !== 'undefined') {
  window.__SB_CLIENTS__!.anon = supabase as SupabaseClient<Database>;
}

// Cache for authenticated clients to prevent multiple instances
let authClientCache: { [key: string]: SupabaseClient<Database> } = {};
let authClientPromises: { [key: string]: Promise<SupabaseClient<Database>> } = {};
let cacheTimeout: NodeJS.Timeout | null = null;

// Function to get Supabase client with Clerk JWT token
export const getSupabaseWithClerkJWT = async () => {
  // Get the Clerk session token for authentication
  if (typeof window !== 'undefined' && window.Clerk) {
    try {
      const currentUserId = window.Clerk.user?.id;
      
      if (!currentUserId) {
        console.log('No Clerk user found, using anonymous client');
        return supabase;
      }
      
      const cacheKey = `auth_${currentUserId}`;
      window.__SB_CLIENTS__ = window.__SB_CLIENTS__ || {};
      window.__SB_CLIENT_PROMISES__ = window.__SB_CLIENT_PROMISES__ || {};
      
      // Return cached client if available
      if (window.__SB_CLIENTS__![cacheKey]) return window.__SB_CLIENTS__![cacheKey];
      if (window.__SB_CLIENT_PROMISES__![cacheKey]) return await window.__SB_CLIENT_PROMISES__![cacheKey];
      if (authClientCache[cacheKey]) return authClientCache[cacheKey];
      if (authClientPromises[cacheKey]) return await authClientPromises[cacheKey];
      
      // Try to get the session token with Supabase template
      let session;
      try {
        const promise = (async () => {
          session = await window.Clerk!.session!.getToken({ template: 'supabase' });
          if (session) {
            // Create a new client with the Clerk JWT token; avoid GoTrue auth persistence
            const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
              global: { headers: { Authorization: `Bearer ${session}` } },
              auth: {
                persistSession: false,
                autoRefreshToken: false,
                storageKey: `sb-calone-authed-${currentUserId}`,
              },
            });
            authClientCache[cacheKey] = client;
            if (typeof window !== 'undefined') {
              window.__SB_CLIENTS__![cacheKey] = client;
            }
            if (cacheTimeout) clearTimeout(cacheTimeout);
            cacheTimeout = setTimeout(() => {
              authClientCache = {};
              authClientPromises = {};
              if (typeof window !== 'undefined') {
                window.__SB_CLIENTS__ = {};
                window.__SB_CLIENT_PROMISES__ = {};
              }
            }, 10 * 60 * 1000);
            return client;
          }
          throw new Error('No Clerk session token (supabase template)');
        })();
        authClientPromises[cacheKey] = promise;
        if (typeof window !== 'undefined') {
          window.__SB_CLIENT_PROMISES__![cacheKey] = promise;
        }
        return await promise;
      } catch (templateError) {
        console.warn('Supabase JWT template not configured in Clerk, falling back to default token');
        // Fallback to default token if template doesn't exist
        try {
          const fallbackPromise = (async () => {
            session = await window.Clerk!.session!.getToken();
            if (session) {
              const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
                global: { headers: { Authorization: `Bearer ${session}` } },
                auth: { persistSession: false, autoRefreshToken: false, storageKey: `sb-calone-authed-${currentUserId}` },
              });
              authClientCache[cacheKey] = client;
              if (typeof window !== 'undefined') {
                window.__SB_CLIENTS__![cacheKey] = client;
              }
              return client;
            }
            throw new Error('No Clerk session token (default)');
          })();
          authClientPromises[cacheKey] = fallbackPromise;
          if (typeof window !== 'undefined') {
            window.__SB_CLIENT_PROMISES__![cacheKey] = fallbackPromise;
          }
          return await fallbackPromise;
        } catch (defaultTokenError) {
          console.error('Failed to get any Clerk token:', defaultTokenError);
        }
      }
    } catch (error) {
      console.error('Error getting Clerk session token:', error);
    }
  }
  
  // Fallback to the regular anonymous client if no Clerk token is available
  console.log('Using anonymous Supabase client');
  return supabase;
};

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  return typeof window !== 'undefined' && 
         window.Clerk?.user?.id !== undefined;
};

// Helper function to get current user ID from Clerk
export const getCurrentClerkUserId = (): string | null => {
  if (typeof window !== 'undefined' && window.Clerk?.user?.id) {
    return window.Clerk.user.id;
  }
  return null;
};
